import { fetchEntriesByType } from './ContentfulEntries';
import {
  ContentfulTypes,
  ProductTestKit,
  ShopFormula,
  ShopTest,
  ProductFormulas,
  ContentfulIncludes,
  ContentfulShopTestEntry,
  ContentfulShopFormulaEntry,
  ContentfulLink,
  ContentfulResponse,
  ContentfulProductTestKitEntry,
  ContentfulProductFormulaEntry,
} from './ContentfulTypes';
import {
  mapToProductTestKit,
  getLinkedEntryFromIncludes,
  mapToProductFormula,
} from './ContentfulUtil';

export const getShopPageTests = async (
  options: Record<string, unknown> = {},
): Promise<ShopTest | null> => {
  try {
    console.log('📡 Making API request to Contentful...');
    const response = await fetchEntriesByType<
      ContentfulShopTestEntry['fields']
    >(ContentfulTypes.SHOP_PAGE_TEST, {
      include: 2, // Include 2 levels of linked entries (shopTest -> testKit -> asset)
      limit: 10,
      ...options,
    });

    if (!response) {
      console.log('⚠️ No shop tests returned from fetchEntriesByType');
      return null;
    }

    // Type assertion for response to match our ContentfulResponse interface
    const shopTests =
      response as unknown as ContentfulResponse<ContentfulShopTestEntry>;
    const includes = shopTests.includes as ContentfulIncludes;

    const mappedShopTests = shopTests.items.map((shopTestEntry) => {
      // Create ShopTest object
      const shopTest: ShopTest = {
        id: shopTestEntry.sys.id,
        title: shopTestEntry.fields.title
          ? String(shopTestEntry.fields.title)
          : undefined,
        tests: [],
      };

      // Process linked testKits if they exist
      if (
        shopTestEntry.fields.tests &&
        Array.isArray(shopTestEntry.fields.tests)
      ) {
        try {
          // Map each linked test kit and add successful mappings immediately
          const mappedTests: ProductTestKit[] = [];

          for (const testKitLink of shopTestEntry.fields.tests) {
            let mappedTestKit: ProductTestKit | null = null;

            if (
              testKitLink.sys &&
              testKitLink.sys.type === 'Link' &&
              testKitLink.sys.linkType === 'Entry'
            ) {
              const linkedEntry = getLinkedEntryFromIncludes(
                testKitLink.sys.id,
                includes,
              );

              if (linkedEntry) {
                // Cast the entry to the right type - we know it should be a ProductTestKit
                const typedEntry =
                  linkedEntry as unknown as ContentfulProductTestKitEntry;

                try {
                  mappedTestKit = mapToProductTestKit(typedEntry, includes);
                } catch (mappingError) {
                  console.error(
                    `❌ Error in mapToProductTestKit:`,
                    mappingError,
                  );
                }
              }
            } else if (testKitLink.sys && testKitLink.sys.type === 'Entry') {
              // Direct Entry reference - unlikely in API but handling for completeness
              // Cast the entry to the right type
              const typedEntry =
                testKitLink as unknown as ContentfulProductTestKitEntry;

              try {
                mappedTestKit = mapToProductTestKit(typedEntry, includes);
              } catch (mappingError) {
                console.error(
                  `❌ Error in mapToProductTestKit (direct):`,
                  mappingError,
                );
              }
            }

            // Add the product immediately if mapping was successful
            if (mappedTestKit) {
              mappedTests.push(mappedTestKit);
              console.log(
                `✅ Successfully mapped test kit: ${mappedTestKit.title} (testId: ${mappedTestKit.testId})`,
              );
            } else {
              console.warn(
                `⚠️ Failed to map test kit link: ${testKitLink.sys?.id || 'unknown'}`,
              );
            }
          }

          console.log(
            `📊 Final result: ${mappedTests.length} test kits mapped successfully`,
          );
          shopTest.tests = mappedTests;
        } catch (e: unknown) {
          const error = e as Error;
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
          });
        }
      } else {
        console.log('ℹ️ No linked test kits found for this shop test');
      }
      return shopTest;
    });

    return mappedShopTests[0];
  } catch (error: unknown) {
    const err = error as Error;
    console.error('Error details:', {
      message: err.message,
      stack: err.stack,
    });
    return null;
  }
};

export const getShopPageFormulas = async (
  options: Record<string, unknown> = {},
): Promise<ShopFormula[] | null> => {
  try {
    console.log('📡 Making API request to Contentful for shop formulas...');
    const response = await fetchEntriesByType<
      ContentfulShopFormulaEntry['fields']
    >(ContentfulTypes.SHOP_PAGE_FORMULAS, {
      include: 2, // Include 2 levels of linked entries
      limit: 10,
      ...options,
    });

    if (!response) {
      console.log('⚠️ No shop formulas returned from fetchEntriesByType');
      return null;
    }

    // Type assertion for response to match our ContentfulResponse interface
    const shopFormulas =
      response as unknown as ContentfulResponse<ContentfulShopFormulaEntry>;
    const includes = shopFormulas.includes as ContentfulIncludes;

    const mappedShopFormulas = shopFormulas.items.map((shopFormulaEntry) => {
      // Create ShopFormula object
      const shopFormula: ShopFormula = {
        tabName: shopFormulaEntry.fields.tabName
          ? String(shopFormulaEntry.fields.tabName)
          : undefined,
        title: shopFormulaEntry.fields.title as
          | Record<string, string>
          | undefined,
        subtitle: shopFormulaEntry.fields.subtitle as
          | Record<string, string>
          | undefined,
        promo: shopFormulaEntry.fields.promo,
        couponCode: shopFormulaEntry.fields.couponCode
          ? String(shopFormulaEntry.fields.couponCode)
          : undefined,
        offerDuration: shopFormulaEntry.fields.offerDuration
          ? String(shopFormulaEntry.fields.offerDuration)
          : undefined,
        products: [],
        discountPercentage: shopFormulaEntry.fields.discountPercentage
          ? Number(shopFormulaEntry.fields.discountPercentage)
          : undefined,
      };

      // Process linked product formulas if they exist
      if (
        shopFormulaEntry.fields.products &&
        Array.isArray(shopFormulaEntry.fields.products)
      ) {
        try {
          // Map each linked product formula
          shopFormula.products = shopFormulaEntry.fields.products
            .map((productFormulaLink: ContentfulLink) => {
              if (
                productFormulaLink.sys &&
                productFormulaLink.sys.type === 'Link' &&
                productFormulaLink.sys.linkType === 'Entry'
              ) {
                const linkedEntry = getLinkedEntryFromIncludes(
                  productFormulaLink.sys.id,
                  includes,
                );
                if (linkedEntry) {
                  // Cast the entry to the right type
                  const typedEntry =
                    linkedEntry as unknown as ContentfulProductFormulaEntry;
                  return mapToProductFormula(typedEntry, includes);
                }
              } else if (
                productFormulaLink.sys &&
                productFormulaLink.sys.type === 'Entry'
              ) {
                // Cast the entry to the right type
                const typedEntry =
                  productFormulaLink as unknown as ContentfulProductFormulaEntry;
                return mapToProductFormula(typedEntry, includes);
              }
              return null;
            })
            .filter(Boolean) as ProductFormulas[]; // Filter out any nulls
        } catch (e: unknown) {
          const error = e as Error;
          console.error('Error mapping product formulas:', {
            message: error.message,
            stack: error.stack,
          });
        }
      } else {
        console.log(
          'ℹ️ No linked product formulas found for this shop formula',
        );
      }
      return shopFormula;
    });

    return mappedShopFormulas;
  } catch (error: unknown) {
    const err = error as Error;
    console.error('Error fetching shop formulas:', {
      message: err.message,
      stack: err.stack,
    });
    return null;
  }
};
